/**
 * URL Normalization Service
 *
 * Centralized service to handle environment-based URL normalization for all backend responses.
 * Solves the structural issue where backend always returns production URLs regardless of environment.
 *
 * Key Features:
 * - Detects and caches environment once per session
 * - Normalizes storage URLs (metadata.videoUrl, metadata.fileUrl, files[].url, thumbnailUrl, etc.)
 * - Provides consistent URL transformation across the entire application
 * - Includes handshake protocol support for environment synchronization
 */

import { detectEnvironment, getBackendUrl } from '../utils/environment.config';
import { Asset } from '../types/asset.types';

export interface EnvironmentSession {
  environment: 'development' | 'staging' | 'production';
  backendUrl: string;
  storageUrlPattern: string;
  detected: boolean;
  sessionId: string;
  timestamp: number;
}

export interface URLNormalizationConfig {
  environment: 'development' | 'staging' | 'production';
  urlMappings: {
    production: string;
    staging: string;
    development: string;
  };
}

class URLNormalizationService {
  private session: EnvironmentSession | null = null;
  private config: URLNormalizationConfig;
  // 🔧 CRITICAL FIX: Reduced from 30 minutes to 5 minutes
  // 30 minutes was too long - users can switch environments multiple times
  // 5 minutes is more responsive while still providing caching benefits
  // Reduces window for stale environment sessions during low connectivity
  private readonly SESSION_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor() {
    // Define storage URL patterns for each environment
    this.config = {
      environment: 'development',
      urlMappings: {
        production: 'https://storage.googleapis.com/nna_registry_assets_prod/',
        staging: 'https://storage.googleapis.com/nna_registry_assets_stg/',
        development: 'https://storage.googleapis.com/nna_registry_assets_dev/',
      },
    };

    this.initializeSession();
  }

  /**
   * Detect which bucket is being used in a URL (handles legacy reviz-assets bucket)
   */
  private detectBucketInUrl(url: string): string | null {
    const bucketMatch = url.match(/storage\.googleapis\.com\/([^\/]+)\//);
    return bucketMatch ? bucketMatch[1] : null;
  }

  /**
   * Initialize environment session with caching
   * 🔧 FIX: Added environment validation to prevent cross-environment data leakage
   */
  private initializeSession(): void {
    try {
      // Try to restore from sessionStorage first
      const cachedSession = sessionStorage.getItem(
        'nna-url-normalization-session'
      );
      if (cachedSession) {
        const parsed = JSON.parse(cachedSession);
        const now = Date.now();

        // 🔧 CRITICAL FIX: Validate session environment matches current hostname
        // This prevents cross-environment data leakage when:
        // 1. User switches from production to dev in same browser
        // 2. Session expires during low connectivity
        // 3. Hostname changes (Vercel branch rebase, etc.)
        const currentEnvironment = detectEnvironment();
        const isTimeValid = now - parsed.timestamp < this.SESSION_DURATION;
        const isEnvironmentValid = parsed.environment === currentEnvironment;

        if (isTimeValid && isEnvironmentValid) {
          this.session = parsed;
          console.log('✅ [URL NORMALIZATION] Restored VALIDATED session:', {
            environment: parsed.environment,
            sessionId: parsed.sessionId,
            age: Math.round((now - parsed.timestamp) / 1000) + 's',
          });
          return;
        } else if (!isEnvironmentValid) {
          console.warn('⚠️ [URL NORMALIZATION] Session environment mismatch - clearing stale session:', {
            cached: parsed.environment,
            current: currentEnvironment,
            hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
          });
          // Clear stale session to prevent cross-environment data leakage
          sessionStorage.removeItem('nna-url-normalization-session');
        } else {
          console.log('⏰ [URL NORMALIZATION] Session expired, creating new session');
        }
      }
    } catch (error) {
      console.warn('⚠️ [URL NORMALIZATION] Failed to restore session:', error);
    }

    // Create new session
    this.createNewSession();
  }

  /**
   * Create a new environment session
   */
  private createNewSession(): void {
    const environment = detectEnvironment();
    const backendUrl = getBackendUrl(environment);
    const sessionId = this.generateSessionId();

    this.session = {
      environment,
      backendUrl,
      storageUrlPattern: this.config.urlMappings[environment],
      detected: true,
      sessionId,
      timestamp: Date.now(),
    };

    // Cache in sessionStorage
    try {
      sessionStorage.setItem(
        'nna-url-normalization-session',
        JSON.stringify(this.session)
      );
    } catch (error) {
      console.warn('⚠️ [URL NORMALIZATION] Failed to cache session:', error);
    }

    console.log('🚀 [URL NORMALIZATION] New session created:', {
      environment: this.session.environment,
      sessionId: this.session.sessionId,
      storagePattern: this.session.storageUrlPattern,
    });
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `nna-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current environment session
   */
  public getSession(): EnvironmentSession {
    if (!this.session) {
      this.initializeSession();
    }
    return this.session!;
  }

  /**
   * Normalize a single URL based on current environment
   */
  public normalizeUrl(url: string | null | undefined): string | null {
    if (!url || typeof url !== 'string') {
      return null;
    }

    const session = this.getSession();

    // Check if URL needs normalization (contains storage.googleapis.com)
    if (!url.includes('storage.googleapis.com')) {
      return url;
    }

    // Detect current bucket in URL
    const currentBucket = this.detectBucketInUrl(url);
    if (!currentBucket) {
      return url; // Can't parse bucket, return unchanged
    }

    // Extract target bucket name from session storage pattern
    const targetBucket = session.storageUrlPattern.match(/storage\.googleapis\.com\/([^\/]+)\//)?.[1];
    if (!targetBucket) {
      console.warn('⚠️ [URL NORMALIZATION] Could not extract target bucket from pattern');
      return url;
    }

    // Skip normalization if already using correct bucket
    if (currentBucket === targetBucket) {
      return url;
    }

    // Normalize URL by replacing bucket name
    // This handles:
    // - nna_registry_assets_prod → nna_registry_assets_dev
    // - nna_registry_assets_stg → nna_registry_assets_dev
    // - reviz-assets (legacy) → nna_registry_assets_dev
    const normalizedUrl = url.replace(
      `storage.googleapis.com/${currentBucket}/`,
      `storage.googleapis.com/${targetBucket}/`
    );

    if (normalizedUrl !== url) {
      console.log('🔄 [URL NORMALIZATION] Normalized URL:', {
        from: currentBucket,
        to: targetBucket,
        original: url.substring(0, 80) + '...',
        normalized: normalizedUrl.substring(0, 80) + '...',
        environment: session.environment,
      });
    }

    return normalizedUrl;
  }

  /**
   * Normalize an entire asset object
   */
  public normalizeAsset(asset: Asset): Asset {
    if (!asset) return asset;

    const normalized: Asset = { ...asset };

    // Normalize metadata URL fields (videoUrl, fileUrl)
    if (asset.metadata && typeof asset.metadata === 'object') {
      const meta = { ...asset.metadata };
      if (meta.videoUrl) {
        meta.videoUrl = this.normalizeUrl(meta.videoUrl) || meta.videoUrl;
      }
      if (meta.fileUrl) {
        meta.fileUrl = this.normalizeUrl(meta.fileUrl) || meta.fileUrl;
      }
      normalized.metadata = meta;
    }
    if ((asset as any).videoUrl) {
      (normalized as any).videoUrl =
        this.normalizeUrl((asset as any).videoUrl) || (asset as any).videoUrl;
    }

    // Normalize file URLs
    if (asset.files && Array.isArray(asset.files)) {
      normalized.files = asset.files.map(file => ({
        ...file,
        url: this.normalizeUrl(file.url) || file.url,
        thumbnailUrl: file.thumbnailUrl
          ? this.normalizeUrl(file.thumbnailUrl) || file.thumbnailUrl
          : file.thumbnailUrl,
      }));
    }

    // Normalize album art URL in songMetadata
    if (asset.songMetadata && typeof asset.songMetadata === 'object') {
      const songMetadata =
        typeof asset.songMetadata === 'string'
          ? JSON.parse(asset.songMetadata)
          : asset.songMetadata;

      if (songMetadata.albumArtUrl) {
        songMetadata.albumArtUrl =
          this.normalizeUrl(songMetadata.albumArtUrl) ||
          songMetadata.albumArtUrl;
        normalized.songMetadata = songMetadata;
      }
    }

    // Normalize any other URL fields that might exist
    if (asset.albumArt) {
      normalized.albumArt = this.normalizeUrl(asset.albumArt) || asset.albumArt;
    }

    return normalized;
  }

  /**
   * Normalize an array of assets
   */
  public normalizeAssets(assets: Asset[]): Asset[] {
    if (!Array.isArray(assets)) return assets;
    return assets.map(asset => this.normalizeAsset(asset));
  }

  /**
   * Normalize backend API response
   */
  public normalizeApiResponse<T>(response: T): T {
    if (!response || typeof response !== 'object') {
      return response;
    }

    // Handle different response structures
    if (Array.isArray(response)) {
      // Array of assets
      return this.normalizeAssets(response as any) as T;
    }

    if ('data' in response) {
      const responseObj = response as any;

      if (Array.isArray(responseObj.data)) {
        // Paginated response with asset array
        return {
          ...responseObj,
          data: this.normalizeAssets(responseObj.data),
        } as T;
      } else if (responseObj.data && typeof responseObj.data === 'object') {
        // Single asset response
        return {
          ...responseObj,
          data: this.normalizeAsset(responseObj.data),
        } as T;
      }
    }

    // Single asset object - check for more specific Asset properties
    if (
      response &&
      typeof response === 'object' &&
      ('files' in response || 'metadata' in response) &&
      'id' in response &&
      'name' in response
    ) {
      return this.normalizeAsset(response as unknown as Asset) as T;
    }

    return response;
  }

  /**
   * Validate current session and refresh if needed
   * 🔧 NEW: Called when network reconnects to ensure environment is still correct
   * This prevents stale sessions from persisting after low connectivity periods
   */
  public validateAndRefreshSession(): void {
    const currentEnvironment = detectEnvironment();

    if (!this.session || this.session.environment !== currentEnvironment) {
      console.warn('⚠️ [URL NORMALIZATION] Environment changed or invalid, creating new session:', {
        previousEnvironment: this.session?.environment || 'none',
        currentEnvironment,
      });

      // Clear stale session
      sessionStorage.removeItem('nna-url-normalization-session');

      // Create new session
      this.createNewSession();
    } else {
      console.log('✅ [URL NORMALIZATION] Session environment still valid:', {
        environment: this.session.environment,
        age: Math.round((Date.now() - this.session.timestamp) / 1000) + 's',
      });
    }
  }

  /**
   * Environment handshake with backend
   */
  public async performEnvironmentHandshake(): Promise<boolean> {
    try {
      const session = this.getSession();
      const handshakePayload = {
        sessionId: session.sessionId,
        frontendEnvironment: session.environment,
        timestamp: Date.now(),
      };

      console.log(
        '🤝 [URL NORMALIZATION] Initiating environment handshake:',
        handshakePayload
      );

      // Send handshake request (implement when backend supports it)
      // const response = await fetch(`${session.backendUrl}/api/environment/handshake`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(handshakePayload)
      // });

      // For now, just log the attempt
      console.log(
        '🔮 [URL NORMALIZATION] Handshake protocol ready for backend implementation'
      );

      return true;
    } catch (error) {
      console.error('❌ [URL NORMALIZATION] Handshake failed:', error);
      return false;
    }
  }

  /**
   * Force refresh session (useful for testing or environment changes)
   */
  public refreshSession(): void {
    try {
      sessionStorage.removeItem('nna-url-normalization-session');
    } catch (error) {
      console.warn(
        '⚠️ [URL NORMALIZATION] Failed to clear session cache:',
        error
      );
    }

    this.session = null;
    this.createNewSession();
    console.log('🔄 [URL NORMALIZATION] Session refreshed');
  }

  /**
   * Get normalization statistics
   */
  public getStats(): {
    session: EnvironmentSession;
    normalizedCount: number;
    cacheAge: number;
  } {
    const session = this.getSession();
    return {
      session,
      normalizedCount: 0, // Could implement counter if needed
      cacheAge: Date.now() - session.timestamp,
    };
  }
}

// Export singleton instance
export const urlNormalizationService = new URLNormalizationService();

// Convenience functions
export const normalizeUrl = (url: string | null | undefined): string | null =>
  urlNormalizationService.normalizeUrl(url);

export const normalizeAsset = (asset: Asset): Asset =>
  urlNormalizationService.normalizeAsset(asset);

export const normalizeAssets = (assets: Asset[]): Asset[] =>
  urlNormalizationService.normalizeAssets(assets);

export const normalizeApiResponse = <T>(response: T): T =>
  urlNormalizationService.normalizeApiResponse(response);

export default urlNormalizationService;
